/**
 * Helper functions for Profile page
 */

/**
 * Resizes an image file to a maximum width of 500px while maintaining aspect ratio
 * @param file - The image file to resize
 * @returns Promise that resolves to a Blob or null if resizing fails
 */
export const resizeImage = (file: File): Promise<Blob | null> => {
  return new Promise(resolve => {
    const img = new Image();
    img.src = URL.createObjectURL(file);
    img.onload = () => {
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');
      if (!ctx) return;

      const scale = 500 / img.width;
      canvas.width = 500;
      canvas.height = img.height * scale;
      ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
      canvas.toBlob(blob => {
        if (blob) {
          resolve(blob);
        } else {
          resolve(null);
        }
      }, file.type);
    };
  });
};

/**
 * Validates if an email address is in a valid format
 * @param email - The email address to validate
 * @returns true if the email is valid, false otherwise
 */
export const isEmailValid = (email: any): boolean => {
  if (!email) return false;
  const re = /\S+@\S+\.\S+/;
  return re.test(email);
};

/**
 * Validates if a phone number is in a valid format (10 digits, numeric only)
 * @param number - The phone number to validate
 * @returns true if the phone number is valid, false otherwise
 */
export const isPhoneNumberFormatValid = (number: string): boolean => {
  if (!number) return false;
  if (number.length === 10) {
    const isnum = /^\d+$/.test(number);
    return isnum;
  }
  return false;
};

/**
 * Validates if a phone number is valid (10 digits, numeric only)
 * This is a convenience function that checks the phone number format
 * @param phoneNumber - The phone number to validate
 * @returns true if the phone number is valid, false otherwise
 */
export const isPhoneNumberValid = (phoneNumber: string | undefined): boolean => {
  if (!phoneNumber) return false;
  return phoneNumber.length === 10 && /^\d+$/.test(phoneNumber);
};

/** Result of phone number helper state: message to show, whether to show warning icon, and whether field is in error state */
export interface PhoneNumberHelperState {
  text: string;
  showWarningIcon: boolean;
  isError: boolean;
}

/**
 * Derives the phone number field helper message, whether to show the warning icon, and error state.
 * Use this single source of truth for TextField error prop and helperText content.
 */
export function getPhoneNumberHelperState(params: {
  number: string | undefined;
  isPhoneValid: boolean;
  isVerified: boolean;
  originalVerifiedNumber: string | undefined;
  otpGenerated: boolean;
}): PhoneNumberHelperState {
  const { number, isPhoneValid, isVerified, originalVerifiedNumber, otpGenerated } = params;

  if (!number) {
    return { text: '', showWarningIcon: false, isError: false };
  }
  if (!isPhoneValid) {
    return {
      text: 'Please enter a valid phone number',
      showWarningIcon: true,
      isError: true,
    };
  }
  const isNewNumberNeedingVerification =
    isVerified && !!originalVerifiedNumber && number !== originalVerifiedNumber;
  if (isNewNumberNeedingVerification) {
    return {
      text: 'Please verify the new number.',
      showWarningIcon: true,
      isError: true,
    };
  }
  if (isVerified) {
    return { text: 'Phone number verified', showWarningIcon: false, isError: false };
  }
  if (!otpGenerated) {
    return {
      text: 'Please verify your phone number',
      showWarningIcon: true,
      isError: false,
    };
  }
  return {
    text: 'OTP sent on WhatsApp. Please enter the OTP to verify.',
    showWarningIcon: false,
    isError: false,
  };
}

/**
 * Interface for OTP handler dependencies
 */
export interface OtpHandlerDependencies {
  phoneNumber: string;
  setVerificationId: (id: string) => void;
  setOtpGenerated: (generated: boolean) => void;
  setOtpValue: (value: string) => void;
  refetchVerificationStatus?: () => Promise<any>;
  generateOtpMutation: {
    mutateAsync: (params: { endpoint: string; payload: any }) => Promise<any>;
  };
  setResendTimer?: (seconds: number) => void;
}

/**
 * Generic function to send OTP request
 * @param dependencies - Object containing all necessary dependencies
 * @returns Promise that resolves to the API response result
 */
const sendOtpRequest = async (dependencies: {
  phoneNumber: string;
  generateOtpMutation: {
    mutateAsync: (params: { endpoint: string; payload: any }) => Promise<any>;
  };
}): Promise<any> => {
  const { phoneNumber, generateOtpMutation } = dependencies;

  const result = await generateOtpMutation.mutateAsync({
    endpoint: 'newUsers/sendOTPForUserVerification',
    payload: {
      verificationType: 'PHONE_NUMBER',
      phoneNumber: phoneNumber,
      emailAddress: '',
    },
  });

  return result;
};

/**
 * Extracts verificationId from the API response
 * @param result - The API response
 * @returns The verificationId if found, undefined otherwise
 */
const extractVerificationId = (result: any): string | undefined => {
  return result?.data?.verificationId || (result as any).verificationId || result?.verificationId;
};

/**
 * Determines if OTP was successfully generated based on the response
 * @param result - The API response
 * @returns true if OTP was successfully generated, false otherwise
 */
const isOtpGenerationSuccessful = (result: any): boolean => {
  // Check if response has status and message (wrapped response)
  if (result?.status === 200) {
    // If verification is already completed, it's not a new generation
    return result.message !== 'Verification has already been completed.';
  }
  // If response has message "OTP sent successfully" or data with verificationId
  if (result?.message === 'OTP sent successfully' || result?.data?.verificationId) {
    return true;
  }
  // If response is just the payload (direct response), treat as success
  if (result?.verificationType && result?.phoneNumber) {
    return true;
  }
  // Fallback: if we get any response without error, assume success
  return !!result;
};

/**
 * Interface for verify OTP handler dependencies
 */
export interface VerifyOtpHandlerDependencies {
  otpValue: string;
  verificationId: string;
  phoneNumber: string;
  setIsVerifying: (verifying: boolean) => void;
  setOtpGenerated: (generated: boolean) => void;
  setOtpValue: (value: string) => void;
  setVerificationId: (id: string) => void;
  setOriginalVerifiedPhoneNumber: (number: string) => void;
  verifyOtpMutation: {
    mutateAsync: (params: { endpoint: string; payload: any }) => Promise<any>;
  };
  showError?: (message: string) => void;
}

/**
 * Handles OTP generation
 * @param dependencies - Object containing all necessary dependencies
 */
export const handleGenerateOtp = async (dependencies: OtpHandlerDependencies): Promise<void> => {
  const {
    phoneNumber,
    setVerificationId,
    setOtpGenerated,
    generateOtpMutation,
    refetchVerificationStatus,
    setResendTimer,
  } = dependencies;

  try {
    const result = await sendOtpRequest({ phoneNumber, generateOtpMutation });

    // Extract and set verificationId from response
    const verificationIdValue = extractVerificationId(result);
    if (verificationIdValue) {
      setVerificationId(verificationIdValue);
    }

    // Check if response has status and message (wrapped response)
    if (result?.status === 200) {
      // Check if verification is already completed
      if (result.message === 'Verification has already been completed.') {
        // Refetch verification status to update UI
        if (refetchVerificationStatus) {
          await refetchVerificationStatus();
        }
      } else {
        // OTP generated successfully
        setOtpGenerated(true);
        if (setResendTimer) {
          setResendTimer(60); // Start 1-minute timer
        }
      }
    } else if (isOtpGenerationSuccessful(result)) {
      // OTP generated successfully
      setOtpGenerated(true);
      if (setResendTimer) {
        setResendTimer(60); // Start 1-minute timer
      }
    }
  } catch (error) {
    console.error('Error generating OTP:', error);
  }
};

/**
 * Handles editing phone number (resets OTP state)
 * @param dependencies - Object containing state setters
 */
export const handleEditPhoneNumber = (dependencies: {
  setOtpGenerated: (generated: boolean) => void;
  setOtpValue: (value: string) => void;
  setVerificationId: (id: string) => void;
  setResendTimer?: (seconds: number) => void;
  timerRef?: { current: NodeJS.Timeout | null };
}): void => {
  const { setOtpGenerated, setOtpValue, setVerificationId, setResendTimer, timerRef } = dependencies;
  setOtpGenerated(false);
  setOtpValue('');
  setVerificationId('');
  if (setResendTimer) {
    setResendTimer(0);
  }
  if (timerRef?.current) {
    clearInterval(timerRef.current);
    timerRef.current = null;
  }
};

/**
 * Handles resending OTP
 * @param dependencies - Object containing all necessary dependencies
 */
export const handleResendOtp = async (dependencies: OtpHandlerDependencies): Promise<void> => {
  const { phoneNumber, setVerificationId, setOtpValue, generateOtpMutation, setResendTimer } = dependencies;

  try {
    const result = await sendOtpRequest({ phoneNumber, generateOtpMutation });

    // Extract and set verificationId from response
    const verificationIdValue = extractVerificationId(result);
    if (verificationIdValue) {
      setVerificationId(verificationIdValue);
    }

    // Reset OTP value when resending
    setOtpValue('');
    if (setResendTimer) {
      setResendTimer(60); // Start 1-minute timer again
    }
  } catch (error) {
    console.error('Error resending OTP:', error);
  }
};

/**
 * Handles OTP verification
 * @param dependencies - Object containing all necessary dependencies
 */
export const handleVerifyOtp = async (dependencies: VerifyOtpHandlerDependencies): Promise<void> => {
  const {
    otpValue,
    verificationId,
    phoneNumber,
    setIsVerifying,
    setOtpGenerated,
    setOtpValue,
    setVerificationId,
    setOriginalVerifiedPhoneNumber,
    verifyOtpMutation,
    showError,
  } = dependencies;

  if (!otpValue || otpValue.length !== 6) {
    if (showError) {
      showError('Please enter a valid 6-digit OTP');
    }
    return;
  }
  if (!verificationId) {
    if (showError) {
      showError('Verification ID not found. Please generate OTP again.');
    }
    return;
  }
  setIsVerifying(true);
  try {
    const result = await verifyOtpMutation.mutateAsync({
      endpoint: 'newUsers/verifyOTPAndUpdateForUserVerification',
      payload: {
        verificationId: verificationId,
        otp: otpValue,
      },
    });
    // Check if response has message indicating success
    if (result?.status && result?.isPhoneNumberVerified) {
      setOtpGenerated(false);
      setOtpValue('');
      setVerificationId('');
      // Update the original verified phone number to the current number
      setOriginalVerifiedPhoneNumber(phoneNumber);
    }
  } catch (error) {
    console.error('Error verifying OTP:', error);
  } finally {
    setIsVerifying(false);
  }
};

